import { useCreateContact } from "@jugl-web/domain-resources/contacts/hooks/useCreateContact";
import {
  ClientContact,
  ClientContactConversation,
  UpdateClientContactPayload,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useToast, useTranslations } from "@jugl-web/utils";
import { ContactDetailsFormValues } from "@web-src/modules/client-support/components/ContactDetailsFormFields";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useCallback } from "react";
import { useClientSupportPageContext } from "../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { mergeContactConversations } from "../utils/mergeContactConversations";

export const useIdentifyContact = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { contactsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { selectedChannel, selectedConversation } =
    useClientSupportPageContext();

  const [updateContact, { isLoading: isUpdatingContact }] =
    contactsApi.useUpdateContactMutation();

  const { createContact, isLoading: isCreatingContact } = useCreateContact({
    entityId,
  });

  const getConversationLink =
    useCallback((): ClientContactConversation | null => {
      const convId = selectedConversation?.conv_id;

      if (!convId) {
        return null;
      }

      return {
        conv_id: convId,
        src: selectedChannel,
      };
    }, [selectedConversation, selectedChannel]);

  const handleUpdateContact = useCallback(
    async (contact: ClientContact): Promise<ClientContact | null> => {
      const conversationLink = getConversationLink();

      if (!conversationLink) {
        return null;
      }

      const response = await updateContact({
        entityId,
        contactId: contact.id,
        data: {
          conversations: mergeContactConversations(
            contact.conversations ?? [],
            conversationLink
          ),
        } as UpdateClientContactPayload,
      });

      if (response && "data" in response) {
        toast(
          <span>
            {t(
              {
                id: "client-support-page.customer-profile-updated",
                defaultMessage: "{name} profile was updated",
              },
              {
                name: <span className="font-medium">{contact.name || ""}</span>,
              }
            )}
          </span>
        );

        return response.data;
      }

      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
      return null;
    },
    [entityId, getConversationLink, t, toast, updateContact]
  );

  const handleCreateContact = useCallback(
    async (form: ContactDetailsFormValues): Promise<ClientContact | null> => {
      const conversationLink = getConversationLink();

      if (!conversationLink) {
        return null;
      }

      return createContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        conversations: [conversationLink],
      });
    },
    [createContact, getConversationLink]
  );

  return {
    handleUpdateContact,
    handleCreateContact,
    isUpdatingContact,
    isCreatingContact,
    isSubmitting: isUpdatingContact || isCreatingContact,
  };
};
